import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tab, TabList, TabPanel, TabsContainer } from 'is24-corecss';
import { report } from '../../../../tracking/tracking';
import { OverviewTrackingEvents, TrackingEvents } from '../../../../tracking/trackingEvents';
import { Benefit } from '../../../../@types/benefits';
import { useHasMeinPlus } from '../../../../customHooks/useHasMeinPlus.hook';
import { useIntl } from '@/i18n/hooks/useIntl';
import './tabs.css';

export type TabsProps = {
  benefit: Benefit;
  tabsContent: (JSX.Element | undefined)[];
};

const TABS_CONFIG = {
  overview: {
    id: 'overview',
    labelId: 'modal.tabs.overview',
    defaultLabel: 'Overview',
    trackingKey: 'OVERVIEW_VISITED',
  },
  howItWorks: {
    id: 'howItWorks',
    labelId: 'modal.tabs.howItWorks',
    defaultLabel: 'How it works',
    trackingKey: 'HOW_IT_WORKS_VISITED',
  },
  HowItWorksTab: {
    id: 'HowItWorksTab',
    labelId: 'modal.tabs.howItWorks',
    defaultLabel: 'How it works',
    trackingKey: 'HOW_IT_WORKS_VISITED',
  },
  faq: {
    id: 'Faq',
    labelId: 'modal.tabs.faqs',
    defaultLabel: 'FAQ',
    trackingKey: 'FAQ_VISITED',
  },
  terms: {
    id: 'terms',
    labelId: 'modal.tabs.terms',
    defaultLabel: 'Nutzungsbedingungen',
    trackingKey: 'TERMS_VISITED',
  },
  FAQTab: {
    id: 'Faq',
    labelId: 'modal.tabs.faqs',
    defaultLabel: 'FAQ',
    trackingKey: 'FAQ_VISITED',
  },
} as const;

const TABS_KEYS = Object.keys(TABS_CONFIG);

export const Tabs = ({ benefit, tabsContent }: TabsProps) => {
  const { t } = useIntl();
  const isMeinPlus = useHasMeinPlus();
  const [tabIndex, setTabIndex] = useState(0);
  const benefitTracking = TrackingEvents[benefit as keyof typeof OverviewTrackingEvents];
  const reportedRef = useRef<{ benefit: Benefit | null; reported: boolean }>({ benefit: null, reported: false });

  const activeTabs = tabsContent
    .filter((content): content is JSX.Element => Boolean(content))
    .map((content, index) => {
      const key = TABS_KEYS.includes(content.key?.toString() || '')
        ? content.key?.toString()
        : content.type.displayName || content.type.name || 'overview';

      const config = TABS_CONFIG[key as keyof typeof TABS_CONFIG] || TABS_CONFIG.overview;
      return {
        ...config,
        label: t({ id: config.labelId, defaultMessage: config.defaultLabel }),
        content,
        key: `${config.id}-${index}`,
      };
    });

  useEffect(() => {
    if (reportedRef.current.benefit !== benefit || !reportedRef.current.reported) {
      report(benefitTracking?.OVERVIEW_VISITED, isMeinPlus);
      reportedRef.current = { benefit, reported: true };
    }
  }, [benefit, isMeinPlus, benefitTracking]);

  const handleTabClick = useCallback(
    (newIndex: number) => {
      if (newIndex === tabIndex) return;
      const selected = activeTabs[newIndex];
      const event = benefitTracking?.[selected.trackingKey as keyof typeof benefitTracking];
      if (event) report(event, isMeinPlus);
      setTabIndex(newIndex);
    },
    [tabIndex, activeTabs, isMeinPlus, benefitTracking],
  );

  return (
    <TabsContainer defaultSelected={0} onClick={handleTabClick}>
      <TabList aria-label={t({ id: 'modal.tabs.label', defaultMessage: 'Feature description' })}>
        {activeTabs.map((tab) => (
          <Tab key={tab.key}>{tab.label}</Tab>
        ))}
      </TabList>

      <div className='one-whole padding'>
        {activeTabs.map((tab) => (
          <TabPanel key={tab.key}>
            <div className='grid grid-flex grid-justify-center'>{tab.content}</div>
          </TabPanel>
        ))}
      </div>
    </TabsContainer>
  );
};
